/**
 * /partners/my-listing — Partner self-service listing editor.
 *
 * Every field the partner touches goes into `attraction_edits` with
 * status="pending" and a moderator applies it via /admin/attraction-edits.
 * The live doc is never modified directly from here.
 */
import React, { useEffect, useState } from 'react';
import { useSeo } from '../lib/seo';
import { Helmet } from 'react-helmet-async';
import { ArrowUpCircle, CheckCircle2, Clock, Info, Loader2, Save } from 'lucide-react';
import partnerApi, { getPartnerToken } from '../lib/partnerApi';
import { useNavigate } from 'react-router-dom';
import MembershipRenewalModal from '../components/MembershipRenewalModal';

// Detailed accessibility flags — mirrors components/Accessibility.js ALL_FIELDS
const ACCESS_FIELDS = [
  { key: 'step_free_entrance', label: 'Step-free entrance' },
  { key: 'step_free_between_floors', label: 'Step-free between floors' },
  { key: 'accessible_parking', label: 'Accessible parking' },
  { key: 'accessible_toilet', label: 'Accessible toilet' },
  { key: 'changing_places_toilet', label: 'Changing Places toilet' },
  { key: 'wheelchair_loan', label: 'Wheelchair to borrow' },
  { key: 'hearing_loop', label: 'Hearing loop' },
  { key: 'large_print_available', label: 'Large-print available' },
  { key: 'sensory_friendly_sessions', label: 'Sensory-friendly sessions' },
  { key: 'assistance_dogs_welcome', label: 'Assistance dogs welcome' },
  { key: 'carer_concession', label: 'Carer concession' },
];
const ACCESS_KEYS = ACCESS_FIELDS.map((f) => f.key);

const TIER_COPY = {
  bronze: 'Bronze — description shows up to 60 words publicly, main photo + 1 gallery image',
  silver: 'Silver — up to 320 words, main photo + 4 gallery images',
  gold:   'Gold — uncapped description, main photo + 9 gallery images',
  none:   'Free entry — 1 photograph, your web link and up to 25 words shown publicly. Phone and email are not published. Upgrade to a membership package to unlock more',
};

export default function PartnerMyListingPage() {
  useSeo('My Listing & Events | Trade Partner Area | goLeicestershire');
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, err: '', data: null });
  const [form, setForm] = useState({ accessibility_flags: {} });
  const [saveStatus, setSaveStatus] = useState({ busy: false, ok: null, err: '', upgrade: false });
  const [galleryInput, setGalleryInput] = useState('');
  const [heroUploading, setHeroUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [rightsDeclared, setRightsDeclared] = useState(false);

  useEffect(() => {
    if (!getPartnerToken()) { nav('/partners'); return; }
    partnerApi.get('/partners/my-listing')
      .then(({ data }) => {
        setState({ loading: false, err: '', data });
        // Honour a #submit-event (or other) anchor once the page has rendered
        if (window.location.hash) {
          setTimeout(() => {
            document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
          }, 250);
        }
        const a = data.attraction || {};
        setForm({
          name: a.name || '',
          description: a.description || '',
          summary: a.summary || '',
          hero_image: a.hero_image || '',
          gallery: a.gallery || [],
          opening_hours: a.opening_hours || '',
          contact_phone: a.contact_phone || '',
          contact_email: a.contact_email || '',
          website: a.website || '',
          ticket_price: a.ticket_price || '',
          price_note: a.price_note || '',
          address: a.address || '',
          postcode: a.postcode || '',
          // Venue feature flags — partners can edit everything; display
          // restrictions (if any) are applied later by tier at read time.
          dog_friendly: !!a.dog_friendly,
          family_friendly: !!a.family_friendly,
          free_entry: !!a.free_entry,
          indoor: !!a.indoor,
          outdoor: !!a.outdoor,
          accessible: !!a.accessible,
          // Nested accessibility — notes plus the detailed feature checkboxes
          accessibility_notes: (a.accessibility && a.accessibility.notes) || '',
          accessibility_flags: ACCESS_KEYS.reduce((acc, k) => ({ ...acc, [k]: !!(a.accessibility && a.accessibility[k]) }), {}),
        });
      })
      .catch((e) => {
        if (e?.response?.status === 401) { nav('/partners'); return; }
        setState({ loading: false, err: e?.response?.data?.detail || 'Could not load your listing', data: null });
      });
  }, [nav]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function addGalleryItem() {
    const url = galleryInput.trim();
    if (!url || !/^https?:\/\//i.test(url)) return;
    set('gallery', [...(form.gallery || []), url]);
    setGalleryInput('');
  }
  function removeGallery(i) { set('gallery', (form.gallery || []).filter((_, idx) => idx !== i)); }

  async function submit() {
    setSaveStatus({ busy: true, ok: null, err: '' });
    // Build changes payload — only send fields the partner actually edited.
    const orig = state.data?.attraction || {};
    const changes = {};
    ['name', 'description', 'summary', 'hero_image', 'opening_hours', 'contact_phone', 'contact_email',
     'website', 'ticket_price', 'price_note', 'address', 'postcode'].forEach((k) => {
      if ((form[k] || '') !== (orig[k] || '')) changes[k] = form[k];
    });
    const currentGallery = (orig.gallery || []).join('|');
    const newGallery = (form.gallery || []).join('|');
    if (currentGallery !== newGallery) changes.gallery = form.gallery;
    // Boolean venue features
    ['dog_friendly', 'family_friendly', 'free_entry', 'indoor', 'outdoor', 'accessible'].forEach((k) => {
      if (form[k] !== !!orig[k]) changes[k] = form[k];
    });
    // Accessibility — notes + detailed feature flags merged into the nested dict
    const currentAccessNotes = (orig.accessibility && orig.accessibility.notes) || '';
    const flagsChanged = ACCESS_KEYS.some((k) => form.accessibility_flags[k] !== !!(orig.accessibility && orig.accessibility[k]));
    if (form.accessibility_notes !== currentAccessNotes || flagsChanged) {
      changes.accessibility = { ...(orig.accessibility || {}), ...form.accessibility_flags, notes: form.accessibility_notes };
    }
    if (!Object.keys(changes).length) {
      setSaveStatus({ busy: false, ok: false, err: 'No changes to submit.' });
      return;
    }
    try {
      const photosChanged = 'hero_image' in changes || 'gallery' in changes;
      const { data } = await partnerApi.post('/partners/my-listing/edit', {
        changes,
        image_rights_declared: photosChanged ? rightsDeclared : undefined,
      });
      setSaveStatus({ busy: false, ok: true, err: '' });
      // Refresh the pending queue so the partner sees the row appear.
      const refreshed = await partnerApi.get('/partners/my-listing');
      setState((s) => ({ ...s, data: refreshed.data }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return data;
    } catch (e) {
      setSaveStatus({
        busy: false, ok: false,
        err: e?.response?.data?.detail || 'Could not submit changes.',
        upgrade: e?.response?.status === 402,
      });
    }
  }

  if (state.loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" size={28}/></div>;
  }
  if (state.err || !state.data?.attraction) {
    return (
      <main className="max-w-3xl mx-auto px-5 py-14">
        <h1 className="font-serif text-3xl text-heritage mb-3">Your trade account</h1>
        <p className="text-charcoal/70" data-testid="my-listing-no-venue-msg">
          {state.err || 'No venue listing is linked to your account yet — our team can link one for you (email partners@goleicestershire.com). In the meantime you can still submit events and request a listing below.'}
        </p>
        {!state.err && <PartnerSubmitPanels />}
      </main>
    );
  }

  const a = state.data.attraction;
  const pending = state.data.pending_edits || [];
  const effectiveTier = state.data.effective_tier || 'none';
  const tierCopy = TIER_COPY[effectiveTier] || TIER_COPY.none;

  return (
    <main className="max-w-4xl mx-auto px-5 py-8 sm:py-12">
      <Helmet><title>{`Your listing — ${a.name}`}</title></Helmet>

      <h1 className="font-serif text-3xl sm:text-4xl text-heritage mb-2" data-testid="my-listing-name">Your listing: {a.name}</h1>
      <p className="text-sm text-charcoal/70 mb-1">{a.town}</p>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <p className="text-xs text-charcoal/60 italic">{tierCopy}</p>
        {effectiveTier !== 'gold' && (
          <button onClick={() => setUpgradeOpen(true)} data-testid="my-listing-upgrade-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-heritage border border-heritage/30 rounded-full px-3 py-1 hover:bg-heritage/5">
            <ArrowUpCircle size={13}/> Upgrade for more photos &amp; words
          </button>
        )}
      </div>

      <MembershipRenewalModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        title="Upgrade your membership"
        aboveTier={effectiveTier === 'none' ? null : effectiveTier}
      />

      {/* Quick actions — the submit forms live at the bottom of this page */}
      <div className="flex flex-wrap gap-2 mb-6">
        <a href="#submit-event" data-testid="quick-link-submit-event"
          className="inline-flex items-center gap-1.5 bg-charcoal text-cream text-xs font-semibold px-4 py-2 rounded-full hover:bg-charcoal/85">
          Submit an event ↓
        </a>
        <a href="#request-listing" data-testid="quick-link-request-listing"
          className="inline-flex items-center gap-1.5 border border-charcoal/20 text-xs font-semibold px-4 py-2 rounded-full hover:border-heritage">
          Request another listing ↓
        </a>
      </div>

      {saveStatus.ok && (
        <div className="bg-forest/10 border border-forest/30 rounded p-3 mb-6 text-sm inline-flex items-start gap-2" data-testid="my-listing-save-ok">
          <CheckCircle2 className="text-forest shrink-0 mt-0.5" size={16}/>
          <span>Thanks — a GoLeicestershire editor will review your changes and publish them shortly. You&apos;ll get an email once they&apos;re live.</span>
        </div>
      )}

      {pending.length > 0 && (
        <div className="bg-mustard/10 border border-mustard/30 rounded p-4 mb-6" data-testid="my-listing-pending">
          <p className="text-sm font-semibold text-charcoal inline-flex items-center gap-2"><Clock size={14}/> {pending.length} pending edit{pending.length > 1 ? 's' : ''} awaiting moderator review</p>
          <ul className="text-xs text-charcoal/70 mt-2 space-y-1">
            {pending.map((p) => (
              <li key={p.id}>
                · Submitted {new Date(p.submitted_at).toLocaleString('en-GB')} — fields: {Object.keys(p.changes || {}).join(', ')}
                {p.source === 'ai_launch_draft' && <span className="ml-1 text-heritage font-semibold">✨ Starter draft</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {state.data.has_ai_draft && (
        <div className="bg-heritage/10 border border-heritage/30 rounded p-4 mb-6" data-testid="my-listing-ai-hint">
          <p className="text-sm text-charcoal">
            <span className="font-semibold text-heritage">✨ We&apos;ve drafted a starter listing for you.</span> Look for the &ldquo;Starter draft&rdquo; row above — a
            GoLeicestershire editor will apply it once you&apos;ve reviewed the copy and swapped the placeholder hero image for one of your own photos.
            You can submit further tweaks below.
          </p>
        </div>
      )}

      <div className="bg-cream/60 border border-charcoal/10 rounded p-5 mb-4 text-sm text-charcoal/70 inline-flex items-start gap-2">
        <Info size={16} className="shrink-0 mt-0.5 text-heritage"/>
        <span>Any change you save here goes into a moderator queue — nothing is published until an editor approves it. Accessibility information is especially important for visitors who plan around specific access needs. New to the portal? <a href="/partners/guide" className="text-heritage font-semibold underline" data-testid="my-listing-guide-link">Read the getting-started guide</a>.</span>
      </div>

      <div className="bg-mustard/10 border border-mustard/40 rounded-xl p-5 mb-6" data-testid="my-listing-tier-visibility-note">
        <p className="text-sm font-semibold text-charcoal mb-1.5">Fill in everything — what's shown publicly depends on your membership level</p>
        <p className="text-sm text-charcoal/75">
          Please complete every field that applies to your venue, including all the feature and accessibility tick boxes.
          We keep the full record on file and nothing you enter is lost — but how much of it appears on your public listing depends on your membership:
          <span className="font-medium text-charcoal"> {tierCopy}.</span>
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          {effectiveTier !== 'gold' && (
            <button onClick={() => setUpgradeOpen(true)} data-testid="tier-note-upgrade-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-heritage text-cream rounded-full px-4 py-2 hover:bg-charcoal transition-colors">
              <ArrowUpCircle size={14}/> Upgrade to increase your presence
            </button>
          )}
          <a href="/partners#tiers" data-testid="tier-note-tiers-link" className="text-xs font-semibold text-heritage underline">
            Compare what each membership level includes →
          </a>
        </div>
      </div>

      <div className="space-y-5">
        <Field label="Listing name">
          <input value={form.name} onChange={(e) => set('name', e.target.value)} className={input()} data-testid="my-listing-name" maxLength={120}/>
          <p className="text-xs text-muted mt-1">Name changes are reviewed by the GoLeicestershire team before going live — your listing's web address stays the same.</p>
        </Field>
        <Field label="Short summary (1 line)"><input value={form.summary} onChange={(e) => set('summary', e.target.value)} className={input()} data-testid="my-listing-summary" maxLength={200}/></Field>
        <Field label="Full description">
          <textarea rows={8} value={form.description} onChange={(e) => set('description', e.target.value)} className={input()} data-testid="my-listing-description" maxLength={5000}/>
          {effectiveTier === 'bronze' && <p className="text-xs text-charcoal/60 mt-1">Bronze listings display up to 60 words publicly — longer entries are trimmed with an ellipsis.</p>}
          {effectiveTier === 'silver' && <p className="text-xs text-charcoal/60 mt-1">Silver listings display up to 320 words publicly.</p>}
        </Field>
        <Field label="Hero image">
          <div className="flex flex-wrap items-center gap-2">
            <label className="bg-charcoal text-cream px-4 py-2 rounded text-sm font-semibold cursor-pointer inline-flex items-center gap-2">
              {heroUploading ? 'Uploading…' : 'Upload from your photos'}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" className="hidden" data-testid="my-listing-hero-file"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setHeroUploading(true);
                  try {
                    const fd = new FormData();
                    fd.append('file', f);
                    const r = await partnerApi.post('/partners/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    set('hero_image', r.data.url);
                  } catch { alert('Upload failed — try a smaller image.'); }
                  finally { setHeroUploading(false); e.target.value = ''; }
                }}/>
            </label>
            {form.hero_image && <img src={form.hero_image} alt="" className="h-12 w-20 object-cover rounded border border-charcoal/10"/>}
          </div>
          <input value={form.hero_image} onChange={(e) => set('hero_image', e.target.value)} placeholder="…or paste an image link (https://…)" className={`${input()} mt-2`} data-testid="my-listing-hero"/>
        </Field>

        <div>
          <label className="block text-sm font-semibold text-charcoal mb-1">Gallery photos ({(form.gallery || []).length})</label>
          <div className="flex flex-wrap gap-2 mb-3">
            <label className="bg-charcoal text-cream px-4 py-2 rounded text-sm font-semibold cursor-pointer shrink-0 inline-flex items-center">
              {galleryUploading ? 'Uploading…' : 'Upload photo'}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" className="hidden" data-testid="my-listing-gallery-file"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setGalleryUploading(true);
                  try {
                    const fd = new FormData();
                    fd.append('file', f);
                    const r = await partnerApi.post('/partners/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    set('gallery', [...(form.gallery || []), r.data.url]);
                  } catch { alert('Upload failed — try a smaller image.'); }
                  finally { setGalleryUploading(false); e.target.value = ''; }
                }}/>
            </label>
            <input value={galleryInput} onChange={(e) => setGalleryInput(e.target.value)} placeholder="…or paste a link (https://…)" className={input()}/>
            <button type="button" onClick={addGalleryItem} className="bg-charcoal text-cream px-4 py-2 rounded text-sm font-semibold">Add</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {(form.gallery || []).map((url, i) => (
              <div key={`${url}-${i}`} className="relative group">
                <img src={url} alt="" className="w-full h-24 object-cover rounded"/>
                <button type="button" onClick={() => removeGallery(i)} className="absolute top-1 right-1 bg-heritage text-cream text-xs rounded-full w-6 h-6">×</button>
              </div>
            ))}
          </div>
          <label className="flex items-start gap-2 text-xs text-charcoal/75 bg-mustard/10 border border-mustard/30 rounded-xl p-3 mt-3 cursor-pointer" data-testid="my-listing-rights-label">
            <input type="checkbox" checked={rightsDeclared} onChange={(e) => setRightsDeclared(e.target.checked)} className="mt-0.5" data-testid="my-listing-rights-checkbox"/>
            <span>Photo copyright declaration: I confirm I hold the licence, rights or necessary permission to use the photos on this listing and to display them on the internet, and I accept responsibility for any copyright claims arising from them. <strong>(Required if you change the main photo or gallery.)</strong></span>
          </label>
        </div>

        <Field label="Opening hours"><textarea rows={4} value={form.opening_hours} onChange={(e) => set('opening_hours', e.target.value)} placeholder="e.g. Mon–Fri 10:00–17:00, Sat 09:00–18:00, closed Sun" className={input()} data-testid="my-listing-hours"/></Field>

        <Field label="Venue features">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="my-listing-features">
            {[
              ['dog_friendly', 'Dogs welcome'],
              ['family_friendly', 'Family friendly'],
              ['free_entry', 'Free entry'],
              ['indoor', 'Indoor'],
              ['outdoor', 'Outdoor'],
              ['accessible', 'Wheelchair accessible'],
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-sm border border-charcoal/10 rounded-xl px-3 py-2 cursor-pointer hover:border-heritage/40">
                <input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`my-listing-feature-${k}`}/>
                {label}
              </label>
            ))}
          </div>
        </Field>

        <Field label="Detailed accessibility (tick everything your venue offers)">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="my-listing-access-flags">
            {ACCESS_FIELDS.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 text-sm border border-charcoal/10 rounded-xl px-3 py-2 cursor-pointer hover:border-heritage/40">
                <input type="checkbox" checked={!!form.accessibility_flags[key]}
                  onChange={(e) => set('accessibility_flags', { ...form.accessibility_flags, [key]: e.target.checked })}
                  data-testid={`my-listing-access-${key}`}/>
                {label}
              </label>
            ))}
          </div>
          <p className="text-xs text-charcoal/50 mt-2">Only tick what genuinely applies — visitors with access needs plan around this information.</p>
        </Field>

        <Field label="Accessibility notes (visitor-facing)">
          <textarea rows={4} value={form.accessibility_notes} onChange={(e) => set('accessibility_notes', e.target.value)}
                    placeholder="e.g. Step-free access to ground floor. Accessible toilet on lower level. BSL interpreter available Sundays."
                    className={input()} data-testid="my-listing-access-notes" maxLength={2000}/>
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Website"><input value={form.website} onChange={(e) => set('website', e.target.value)} className={input()}/></Field>
          <Field label="Phone"><input value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} className={input()}/></Field>
          <Field label="Contact email (shown on your listing)"><input value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} className={input()}/></Field>
          <Field label="Ticket price (e.g. £8 adult / £4 child)"><input value={form.ticket_price} onChange={(e) => set('ticket_price', e.target.value)} className={input()}/></Field>
          <Field label="Price notes / booking info"><input value={form.price_note} onChange={(e) => set('price_note', e.target.value)} className={input()}/></Field>
          <Field label="Address"><input value={form.address} onChange={(e) => set('address', e.target.value)} className={input()}/></Field>
          <Field label="Postcode"><input value={form.postcode} onChange={(e) => set('postcode', e.target.value)} className={input()}/></Field>
        </div>

        {saveStatus.err && (
          <div className="text-sm text-heritage flex flex-wrap items-center gap-3">
            <p data-testid="my-listing-error">{saveStatus.err}</p>
            {saveStatus.upgrade && (
              <button onClick={() => setUpgradeOpen(true)} data-testid="my-listing-error-upgrade-btn"
                className="inline-flex items-center gap-1.5 bg-heritage text-white text-xs font-semibold px-4 py-2 rounded-full hover:bg-heritage/90">
                <ArrowUpCircle size={14}/> Upgrade now — pay online
              </button>
            )}
          </div>
        )}
        <button onClick={submit} disabled={saveStatus.busy} data-testid="my-listing-submit"
                className="bg-heritage text-cream px-8 py-3 rounded-full font-semibold hover:bg-heritage/90 disabled:opacity-50 inline-flex items-center gap-2">
          {saveStatus.busy ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>}
          Submit changes for review
        </button>

        <PartnerSubmitPanels />
      </div>
    </main>
  );
}

function PartnerSubmitPanels() {
  const [ev, setEv] = useState({ title: '', start_date: '', end_date: '', town: '', venue: '', url: '', summary: '', description: '', accessibility_info: '', hero_image: '', image_rights_declared: false, is_free: false, price: '' });
  const [nl, setNl] = useState({ name: '', category: 'days_out', town: '', website: '', summary: '', description: '', accessibility_info: '', hero_image: '', image_rights_declared: false });
  const [msg, setMsg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const post = async (path, body, reset, missing) => {
    if (missing.length) {
      setMsg({ ok: false, text: `Please complete the following before submitting: ${missing.join(', ')}.` });
      return;
    }
    setMsg(null);
    try {
      const r = await partnerApi.post(path, body);
      setMsg({ ok: true, text: r.data.message || 'Submitted for review — thank you!' });
      reset();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || 'Submission failed — please check the required fields.' });
    }
  };
  const evMissing = () => {
    const m = [];
    if (!ev.title.trim()) m.push('event title');
    if (!ev.start_date) m.push('start date');
    if (!ev.summary.trim()) m.push('a short description');
    if (!ev.description.trim()) m.push('a full description');
    if (!ev.hero_image.trim()) m.push('a photo');
    if (ev.hero_image.trim() && !ev.image_rights_declared) m.push('the image rights confirmation');
    if (!ev.url.trim()) m.push('a website / booking link');
    if (!ev.accessibility_info.trim()) m.push('access information');
    return m;
  };
  const nlMissing = () => {
    const m = [];
    if (!nl.name.trim()) m.push('venue name');
    if (!nl.summary.trim()) m.push('a short description');
    if (!nl.description.trim()) m.push('a full description');
    if (!nl.hero_image.trim()) m.push('a photo');
    if (nl.hero_image.trim() && !nl.image_rights_declared) m.push('the image rights confirmation');
    if (!nl.website.trim()) m.push('a website link');
    if (!nl.accessibility_info.trim()) m.push('access information');
    return m;
  };
  const uploadImage = (apply) => async (e2) => {
    const f = e2.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await partnerApi.post('/partners/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      apply(r.data.url);
    } catch (e3) {
      setMsg({ ok: false, text: e3?.response?.data?.detail || 'Upload failed — try a smaller JPG or PNG.' });
    }
    setUploading(false);
    e2.target.value = '';
  };
  return (
    <div className="mt-12 grid md:grid-cols-2 gap-6">
      <div className="border border-charcoal/15 rounded-2xl p-5 scroll-mt-24" id="submit-event" data-testid="partner-event-form">
        <h3 className="font-display text-xl mb-1">Submit an event</h3>
        <p className="text-xs text-muted mb-3">Put an event forward for the What's On calendar. Our team reviews every submission before it appears.</p>
        <div className="space-y-2">
          <Field label="Event title *"><input value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} className={input()} data-testid="partner-event-title"/></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Start date *"><input type="date" value={ev.start_date} onChange={(e) => setEv({ ...ev, start_date: e.target.value })} className={input()} data-testid="partner-event-start"/></Field>
            <Field label="End date"><input type="date" value={ev.end_date} onChange={(e) => setEv({ ...ev, end_date: e.target.value })} className={input()}/></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Town"><input value={ev.town} onChange={(e) => setEv({ ...ev, town: e.target.value })} className={input()}/></Field>
            <Field label="Venue"><input value={ev.venue} onChange={(e) => setEv({ ...ev, venue: e.target.value })} className={input()}/></Field>
          </div>
          <Field label="Website / booking link *"><input value={ev.url} onChange={(e) => setEv({ ...ev, url: e.target.value })} placeholder="https://…" className={input()} data-testid="partner-event-url"/></Field>
          <Field label="Ticket price">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-charcoal/75 cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={ev.is_free}
                  onChange={(e) => setEv({ ...ev, is_free: e.target.checked, price: e.target.checked ? '' : ev.price })}
                  data-testid="partner-event-free-checkbox"/>
                Free event
              </label>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-charcoal/50">£</span>
                <input type="number" min="0" step="0.01" value={ev.price} disabled={ev.is_free}
                  onChange={(e) => setEv({ ...ev, price: e.target.value })}
                  placeholder={ev.is_free ? '—' : 'e.g. 12.50'}
                  className={`${input()} pl-7 disabled:opacity-40`} data-testid="partner-event-price"/>
              </div>
            </div>
          </Field>
          <Field label="Event photo *">
            <div>
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" data-testid="partner-event-file"
                onChange={uploadImage((u) => setEv((s) => ({ ...s, hero_image: u })))}
                className="text-xs" />
              {uploading && <span className="text-xs text-charcoal/60 ml-2">Uploading…</span>}
              {ev.hero_image && (
                <div className="flex items-center gap-3 mt-2">
                  <img src={ev.hero_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${ev.hero_image}` : ev.hero_image} alt="Event" className="h-20 w-28 object-cover rounded-lg border border-charcoal/10" data-testid="partner-event-image-preview" />
                  <button type="button" onClick={() => setEv({ ...ev, hero_image: '', image_rights_declared: false })} className="text-xs text-heritage underline" data-testid="partner-event-image-remove">Remove</button>
                </div>
              )}
              <input value={ev.hero_image || ''} onChange={(e2) => setEv({ ...ev, hero_image: e2.target.value })} placeholder="…or paste an image link (https://…)" className={`${input()} mt-2`} data-testid="partner-event-image"/>
            </div>
          </Field>
          <Field label="Short description * (one or two sentences for the event card)">
            <textarea rows={2} maxLength={240} value={ev.summary} onChange={(e) => setEv({ ...ev, summary: e.target.value })} className={input()} data-testid="partner-event-summary"/>
          </Field>
          <Field label="Full description *"><textarea rows={4} value={ev.description} onChange={(e) => setEv({ ...ev, description: e.target.value })} className={input()} data-testid="partner-event-description"/></Field>
          <Field label="Access information * (e.g. step-free access, accessible toilets, parking, assistance dogs)">
            <textarea rows={2} value={ev.accessibility_info} onChange={(e) => setEv({ ...ev, accessibility_info: e.target.value })} className={input()} data-testid="partner-event-access"/>
          </Field>
          {(ev.hero_image || '').trim() !== '' && (
            <label className="flex items-start gap-2 text-xs text-charcoal/75 bg-mustard/10 border border-mustard/30 rounded-xl p-3 cursor-pointer" data-testid="partner-event-rights-label">
              <input type="checkbox" checked={!!ev.image_rights_declared} onChange={(e) => setEv({ ...ev, image_rights_declared: e.target.checked })} className="mt-0.5" data-testid="partner-event-rights-checkbox"/>
              <span>I confirm I hold the licence, rights or necessary permission to use this image and to display it on the internet, and I accept responsibility for any copyright claims arising from it. *</span>
            </label>
          )}
          <button onClick={() => post('/partners/events/submit', ev, () => setEv({ title: '', start_date: '', end_date: '', town: '', venue: '', url: '', summary: '', description: '', accessibility_info: '', hero_image: '', image_rights_declared: false, is_free: false, price: '' }), evMissing())}
            data-testid="partner-event-submit" className="bg-charcoal text-cream text-sm font-semibold px-5 py-2 rounded-full">Submit event for review</button>
        </div>
      </div>
      <div className="border border-charcoal/15 rounded-2xl p-5 scroll-mt-24" id="request-listing" data-testid="partner-new-listing-form">
        <h3 className="font-display text-xl mb-1">Request a new venue listing</h3>
        <p className="text-xs text-muted mb-1.5">Run another venue? Request an additional listing — it goes live once our team approves it, with features matching your membership level.</p>
        <p className="text-xs text-charcoal/70 bg-mustard/10 border border-mustard/30 rounded-lg px-3 py-2 mb-3" data-testid="new-listing-tier-note">
          Give us everything you can — we keep the full record, though what's displayed publicly depends on your membership level.
          {' '}<a href="/partners#tiers" className="text-heritage font-semibold underline" data-testid="new-listing-tiers-link">See what each level includes and how to upgrade →</a>
        </p>
        <div className="space-y-2">
          <Field label="Venue name *"><input value={nl.name} onChange={(e) => setNl({ ...nl, name: e.target.value })} className={input()} data-testid="partner-listing-name"/></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Town"><input value={nl.town} onChange={(e) => setNl({ ...nl, town: e.target.value })} className={input()}/></Field>
            <Field label="Website *"><input value={nl.website} onChange={(e) => setNl({ ...nl, website: e.target.value })} placeholder="https://…" className={input()} data-testid="partner-listing-website"/></Field>
          </div>
          <Field label="Short description * (one or two sentences for the listing card)">
            <textarea rows={2} maxLength={300} value={nl.summary} onChange={(e) => setNl({ ...nl, summary: e.target.value })} className={input()} data-testid="partner-listing-summary"/>
          </Field>
          <Field label="Full description *"><textarea rows={4} value={nl.description} onChange={(e) => setNl({ ...nl, description: e.target.value })} className={input()} data-testid="partner-listing-description"/></Field>
          <Field label="Venue photo *">
            <div>
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" data-testid="partner-listing-file"
                onChange={uploadImage((u) => setNl((s) => ({ ...s, hero_image: u })))} className="text-xs" />
              {uploading && <span className="text-xs text-charcoal/60 ml-2">Uploading…</span>}
              {nl.hero_image && (
                <div className="flex items-center gap-3 mt-2">
                  <img src={nl.hero_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${nl.hero_image}` : nl.hero_image} alt="Venue" className="h-20 w-28 object-cover rounded-lg border border-charcoal/10" data-testid="partner-listing-image-preview" />
                  <button type="button" onClick={() => setNl({ ...nl, hero_image: '', image_rights_declared: false })} className="text-xs text-heritage underline" data-testid="partner-listing-image-remove">Remove</button>
                </div>
              )}
            </div>
          </Field>
          <Field label="Access information * (e.g. step-free access, accessible toilets, parking, assistance dogs)">
            <textarea rows={2} value={nl.accessibility_info} onChange={(e) => setNl({ ...nl, accessibility_info: e.target.value })} className={input()} data-testid="partner-listing-access"/>
          </Field>
          {(nl.hero_image || '').trim() !== '' && (
            <label className="flex items-start gap-2 text-xs text-charcoal/75 bg-mustard/10 border border-mustard/30 rounded-xl p-3 cursor-pointer" data-testid="partner-listing-rights-label">
              <input type="checkbox" checked={!!nl.image_rights_declared} onChange={(e) => setNl({ ...nl, image_rights_declared: e.target.checked })} className="mt-0.5" data-testid="partner-listing-rights-checkbox"/>
              <span>I confirm I hold the licence, rights or necessary permission to use this image and to display it on the internet, and I accept responsibility for any copyright claims arising from it. *</span>
            </label>
          )}
          <button onClick={() => post('/partners/listings/create', nl, () => setNl({ name: '', category: 'days_out', town: '', website: '', summary: '', description: '', accessibility_info: '', hero_image: '', image_rights_declared: false }), nlMissing())}
            data-testid="partner-listing-submit" className="bg-charcoal text-cream text-sm font-semibold px-5 py-2 rounded-full">Request listing</button>
        </div>
      </div>
      {msg && <div className={`md:col-span-2 text-sm font-semibold ${msg.ok ? 'text-forest' : 'text-heritage'}`} data-testid="partner-submit-msg">{msg.text}</div>}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-charcoal mb-1">{label}</span>
      {children}
    </label>
  );
}
function input() { return 'w-full border border-charcoal/20 rounded px-3 py-2 text-sm bg-white'; }
